import React, { useState, useEffect, useMemo } from 'react';
import { 
  Eye, 
  MousePointer, 
  MessageCircle, 
  TrendingUp, 
  ChevronDown, 
  Download,
  Search,
  Filter,
  ArrowUp,
  ArrowDown,
  Loader2,
  BarChart3,
  Users,
  Activity,
  Target,
  Calendar,
  Sparkles,
  Award,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import useClinicianAnalytics from '../hooks/Clinic/useClinicianAnalytics';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      duration: 0.6
    }
  },
  hover: {
    y: -5,
    scale: 1.02,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20
    }
  }
};

const rowVariants = {
  hidden: { opacity: 0, x: -20, scale: 0.98 },
  show: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  },
  hover: {
    scale: 1.01,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 20 },
  show: { 
    opacity: 1, 
    x: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const AnalyticsPage = () => {
  const [dateRange, setDateRange] = useState('Last 7 days');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: analyticsData, loading, error, fetchAnalytics } = useClinicianAnalytics();

  const practiceId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (practiceId) {
      fetchAnalytics(practiceId);
    }
  }, [practiceId, fetchAnalytics]);

  const filteredTherapists = useMemo(() => {
    let therapistsData = null;
    
    if (analyticsData?.data?.data) {
      therapistsData = analyticsData.data.data;
    } else if (analyticsData?.data) {
      therapistsData = analyticsData.data;
    } else if (Array.isArray(analyticsData)) {
      therapistsData = analyticsData;
    }
    
    if (!therapistsData || !Array.isArray(therapistsData)) {
      return [];
    }
    
    return therapistsData.filter((therapist: any) =>
      searchQuery === '' || 
      therapist.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      therapist.title?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [analyticsData, searchQuery]);

  const getChangeIcon = (change: number) => {
    if (change > 0) return <ArrowUp className="w-3 h-3 mr-1" />;
    if (change < 0) return <ArrowDown className="w-3 h-3 mr-1" />;
    return null;
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
          <div className="p-8">
            <motion.div 
              className="flex items-center justify-center h-64"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
                <div className="flex items-center space-x-4">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 className="w-8 h-8 text-purple-600" />
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Loading Analytics</h3>
                    <p className="text-gray-600">Fetching your performance data...</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
          <div className="p-8">
            <motion.div 
              className="bg-red-50/70 backdrop-blur-sm border border-red-200/50 rounded-2xl p-8 shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <div className="w-6 h-6 bg-red-600 rounded-full" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-red-800">Error loading analytics</h3>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!analyticsData) {
    return (
      <DashboardLayout>
        <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
          <div className="p-8">
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 max-w-md mx-auto">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Eye className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Analytics Data</h3>
                <p className="text-gray-600">Analytics data will appear here once available.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen relative">
        {/* Floating Particles Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-2 h-2 bg-purple-400 rounded-full opacity-30"
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-40 right-20 w-3 h-3 bg-blue-400 rounded-full opacity-20"
            animate={{ y: [0, -30, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
          <motion.div
            className="absolute bottom-40 left-1/4 w-1 h-1 bg-indigo-400 rounded-full opacity-40"
            animate={{ y: [0, -15, 0], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
        </div>
        
        <div className="p-8 relative z-10">
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between mb-8"
            variants={fadeInUp}
            initial="hidden"
            animate="show"
          >
            <div>
              <motion.h1 
                className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-purple-800 to-indigo-900 bg-clip-text text-transparent mb-3"
                variants={fadeInUp}
              >
                <BarChart3 className="inline w-8 h-8 mr-3 text-purple-600" />
                Profile Analytics
              </motion.h1>
              <motion.p className="text-gray-600 text-lg" variants={fadeInUp}>
                Track profile performance and therapist engagement metrics
              </motion.p>
            </div>
            <motion.div className="flex items-center gap-4" variants={slideInRight}>
              <div className="relative">
                <select 
                  className="appearance-none bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-lg"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="Today">Today</option>
                  <option value="Yesterday">Yesterday</option>
                  <option value="Last 7 days">Last 7 days</option>
                  <option value="Last 30 days">Last 30 days</option>
                  <option value="Last 90 days">Last 90 days</option>
                  <option value="This month">This month</option>
                  <option value="Last month">Last month</option>
                  <option value="Custom range">Custom range</option>
                </select>
                <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>
              <motion.button 
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Download className="w-4 h-4" />
                Export Data
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            <motion.div 
              variants={cardVariants}
              whileHover="hover"
              className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:bg-white/80 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  +{analyticsData.metadata?.dataPoints?.views || '0'}
                </span>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                {analyticsData.summary?.totalViews?.toLocaleString() || '0'}
              </div>
              <div className="text-sm font-medium text-gray-900">Total Profile Views</div>
              <div className="text-xs text-gray-500 mt-1">All therapist profiles</div>
            </motion.div>

            <motion.div 
              variants={cardVariants}
              whileHover="hover"
              transition={{ delay: 0.1 }}
              className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:bg-white/80 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                  <MousePointer className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  +{analyticsData.metadata?.dataPoints?.clicks || '0'}
                </span>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                {analyticsData.summary?.totalClicks?.toLocaleString() || '0'}
              </div>
              <div className="text-sm font-medium text-gray-900">Total Profile Clicks</div>
              <div className="text-xs text-gray-500 mt-1">Contact button clicks</div>
            </motion.div>

            <motion.div 
              variants={cardVariants}
              whileHover="hover"
              transition={{ delay: 0.2 }}
              className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:bg-white/80 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  +{analyticsData.metadata?.dataPoints?.connections || '0'}
                </span>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                {analyticsData.summary?.totalConnections || '0'}
              </div>
              <div className="text-sm font-medium text-gray-900">Connect Requests</div>
              <div className="text-xs text-gray-500 mt-1">Client inquiries</div>
            </motion.div>

            <motion.div 
              variants={cardVariants}
              whileHover="hover"
              transition={{ delay: 0.3 }}
              className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20 hover:bg-white/80 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  +{analyticsData.summary?.averageConversionRate || '0.0'}%
                </span>
              </div>
              <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-1">
                {analyticsData.summary?.averageConversionRate || '0.0'}%
              </div>
              <div className="text-sm font-medium text-gray-900">Conversion Rate</div>
              <div className="text-xs text-gray-500 mt-1">Requests / Views</div>
            </motion.div>
          </motion.div>

          {/* Performance Table */}
          <motion.div 
            className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20"
            variants={fadeInUp}
            initial="hidden"
            animate="show"
          >
            <div className="p-6 border-b border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <motion.h2 
                    className="text-xl font-bold bg-gradient-to-r from-gray-900 to-purple-800 bg-clip-text text-transparent"
                    variants={fadeInUp}
                  >
                    <Users className="inline w-6 h-6 mr-2 text-purple-600" />
                    Individual Therapist Performance
                  </motion.h2>
                  <motion.p className="text-sm text-gray-600 mt-1" variants={fadeInUp}>
                    Compare engagement metrics across all therapists
                  </motion.p>
                </div>
                <motion.div className="flex items-center gap-3" variants={slideInRight}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input 
                      type="text" 
                      placeholder="Search therapists..." 
                      className="pl-10 pr-4 py-3 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white/50 backdrop-blur-sm shadow-lg border border-white/20"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <motion.button 
                    className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/70 transition-all duration-300 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Filter className="w-4 h-4" />
                    Filter
                  </motion.button>
                </motion.div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-purple-50">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Therapist
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-blue-50 cursor-pointer hover:bg-blue-50/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-blue-600" />
                        Profile Views
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-green-50 cursor-pointer hover:bg-green-50/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <MousePointer className="w-4 h-4 text-green-600" />
                        Profile Clicks
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-purple-50 cursor-pointer hover:bg-purple-50/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-purple-600" />
                        Connect Requests
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-orange-50">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-orange-600" />
                        Conversion Rate
                      </div>
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-indigo-50">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-indigo-600" />
                        Views Trend
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTherapists.map((therapist: any, index: number) => (
                    <motion.tr 
                      key={therapist.id}
                      variants={rowVariants}
                      initial="hidden"
                      animate="show"
                      whileHover="hover"
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/10 hover:bg-white/30 transition-all duration-300"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg">
                            {therapist.name?.charAt(0) || 'T'}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{therapist.name}</div>
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              <Award className="w-3 h-3 text-purple-500" />
                              {therapist.title}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-gray-900 text-lg">{therapist?.metrics?.profileViews?.total || 0}</div>
                        <span className={`flex items-center text-sm ${getChangeColor(therapist?.metrics?.profileViews?.trendValue || 0)}`}>
                          {getChangeIcon(therapist?.metrics?.profileViews?.trendValue || 0)}
                          {therapist?.metrics?.profileViews?.trend || '0%'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-gray-900 text-lg">{therapist?.metrics?.profileClicks?.total || 0}</div>
                        <span className={`flex items-center text-sm ${getChangeColor(therapist?.metrics?.profileClicks?.trendValue || 0)}`}>
                          {getChangeIcon(therapist?.metrics?.profileClicks?.trendValue || 0)}
                          {therapist?.metrics?.profileClicks?.trend || '0%'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-semibold text-gray-900 text-lg">{therapist?.metrics?.connectRequests?.total || 0}</div>
                        <span className={`flex items-center text-sm ${getChangeColor(therapist?.metrics?.connectRequests?.trendValue || 0)}`}>
                          {getChangeIcon(therapist?.metrics?.connectRequests?.trendValue || 0)}
                          {therapist?.metrics?.connectRequests?.trend || '0%'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-lg">{therapist?.metrics?.conversionRates?.overallConversion || '0%'}</span>
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                              style={{ width: `${parseFloat(therapist?.metrics?.conversionRates?.overallConversion || '0')}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className={`flex items-center text-sm font-medium ${getChangeColor(therapist?.metrics?.profileViews?.trendValue || 0)}`}>
                            {getChangeIcon(therapist?.metrics?.profileViews?.trendValue || 0)}
                            {therapist?.metrics?.profileViews?.trend || '0%'}
                          </span>
                          {therapist?.metrics?.profileViews?.trendValue > 0 && (
                            <Sparkles className="w-4 h-4 text-yellow-500 animate-pulse" />
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredTherapists.length === 0 && (
              <motion.div 
                className="text-center py-12"
                variants={fadeInUp}
                initial="hidden"
                animate="show"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No therapists found</h3>
                <p className="text-gray-600">
                  {searchQuery ? 'Try adjusting your search terms' : 'No therapist data available'}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
